import {
  Box,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { escapeForRegEx } from "@tiptap/core";
import { ReactRenderer } from "@tiptap/react";
import type {
  SuggestionOptions,
  SuggestionProps,
  Trigger,
} from "@tiptap/suggestion";
import { Avatar } from "components";
import { Button } from "components/button";
import { InfoIcon, MailIcon } from "components/icons";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AccessSpecifier } from "shared/types";
import type { ChatMember, MemberType } from "shared/types/chatMembers";
import tippy, { Instance as TippyInstance } from "tippy.js";

// ─── Types ───────────────────────────────────────────────────────────

export interface MentionItem {
  id: string;
  label: string;
  email: string | null;
  profileImage: string | null;
  type: "member" | "email" | "no-results";
  memberType?: MemberType;
  allowEmail?: boolean;
}

interface MentionListRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

interface MentionListProps {
  items: MentionItem[];
  command: (item: { id: string; label: string }) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value);

/**
 * Custom findSuggestionMatch that allows `@` and `.` in the query text.
 * The default TipTap implementation uses `[^\s@]*` which breaks at the
 * second `@` in email addresses like `user@example.com`.
 */
const findEmailAwareSuggestionMatch = (
  config: Trigger,
): {
  range: { from: number; to: number };
  query: string;
  text: string;
} | null => {
  const { char, allowedPrefixes, startOfLine, $position } = config;

  const escapedChar = escapeForRegEx(char);
  const prefix = startOfLine ? "^" : "";
  // Allow word chars, @, dots, hyphens, plus signs (common in emails)
  const regexp = new RegExp(`${prefix}(?:^)?${escapedChar}[\\w@.\\-+]*`, "gm");

  const text = $position.nodeBefore?.isText && $position.nodeBefore.text;
  if (!text) return null;

  const textFrom = $position.pos - text.length;
  const match = Array.from(text.matchAll(regexp)).pop();

  if (!match || match.input === undefined || match.index === undefined) {
    return null;
  }

  const matchPrefix = match.input.slice(
    Math.max(0, match.index - 1),
    match.index,
  );
  const matchPrefixIsAllowed = new RegExp(
    `^[${allowedPrefixes?.join("") ?? ""}\\0]?$`,
  ).test(matchPrefix);

  if (allowedPrefixes !== null && !matchPrefixIsAllowed) {
    return null;
  }

  const from = textFrom + match.index;
  const to = from + match[0].length;

  if (from < $position.pos && to >= $position.pos) {
    return {
      range: { from, to },
      query: match[0].slice(char.length),
      text: match[0],
    };
  }

  return null;
};

// ─── MentionList Component (rendered inside tippy popup) ─────────────

const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const theme = useTheme();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [emailConfirmItem, setEmailConfirmItem] =
      useState<MentionItem | null>(null);
    const listRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
      setSelectedIndex(0);
      setEmailConfirmItem(null);
    }, [items]);

    useEffect(() => {
      const container = listRef.current;
      if (!container) return;
      const selected = container.children[selectedIndex] as
        | HTMLElement
        | undefined;
      selected?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex]);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (!item || item.type === "no-results") return;

        if (item.type === "email") {
          setEmailConfirmItem(item);
          return;
        }

        command({ id: item.id, label: item.label });
      },
      [items, command],
    );

    const handleAddEmail = useCallback(() => {
      if (emailConfirmItem) {
        command({ id: emailConfirmItem.id, label: emailConfirmItem.label });
      }
    }, [emailConfirmItem, command]);

    const handleCancelEmail = useCallback(() => {
      setEmailConfirmItem(null);
    }, []);

    const handleEmailConfirmKeyDown = useCallback(
      (event: KeyboardEvent): boolean => {
        if (event.key === "Enter") handleAddEmail();
        else if (event.key === "Escape") handleCancelEmail();
        return true; // Block all keys while confirming
      },
      [handleAddEmail, handleCancelEmail],
    );

    const selectableItems = useMemo(
      () => items.filter((i) => i.type !== "no-results"),
      [items],
    );

    const handleListKeyDown = useCallback(
      (event: KeyboardEvent): boolean => {
        // Nothing selectable — let keystrokes pass through to editor
        if (selectableItems.length === 0) return false;

        if (event.key === "ArrowUp") {
          setSelectedIndex((prev) =>
            prev <= 0 ? selectableItems.length - 1 : prev - 1,
          );
          return true;
        }
        if (event.key === "ArrowDown") {
          setSelectedIndex((prev) =>
            prev >= selectableItems.length - 1 ? 0 : prev + 1,
          );
          return true;
        }
        if (event.key === "Enter") {
          // Find the actual index in `items` for the selected selectable item
          const selectedItem = selectableItems[selectedIndex];
          const realIndex = items.indexOf(selectedItem);
          selectItem(realIndex);
          return true;
        }
        return false;
      },
      [selectableItems, selectItem, selectedIndex, items],
    );

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) =>
        emailConfirmItem
          ? handleEmailConfirmKeyDown(event)
          : handleListKeyDown(event),
    }));

    if (items.length === 0) return null;

    // ─── No results view (typed text shown, not selectable) ──────
    const isNoResults = items.length === 1 && items[0].type === "no-results";

    if (isNoResults) {
      const queryIsEmail =
        items[0].allowEmail !== false && isValidEmail(items[0].label);

      return (
        <Paper
          elevation={3}
          sx={{
            borderRadius: "8px",
            minWidth: "200px",
            overflow: "hidden",
          }}
        >
          {queryIsEmail && (
            <Typography
              variant="body-small"
              sx={{
                color: theme.palette.primary.main,
                px: "12px",
                py: "8px",
              }}
            >
              Use this address : @{items[0].label}
            </Typography>
          )}
          <Typography
            variant="body-small"
            sx={{
              color: theme.palette.lightBg.low,
              px: "12px",
              py: queryIsEmail ? undefined : "8px",
              pb: "8px",
            }}
          >
            We couldn&apos;t find the person you were looking for.
          </Typography>
        </Paper>
      );
    }

    // ─── Email confirmation view ─────────────────────────────────
    if (emailConfirmItem) {
      const { orange } = theme.palette.primitive.system;

      return (
        <Paper
          elevation={3}
          sx={{
            borderRadius: "8px",
            minWidth: "200px",
            overflow: "hidden",
          }}
        >
          {/* Typed email display */}
          <Typography
            variant="body-small"
            sx={{
              color: theme.palette.lightBg.low,
              px: "12px",
              py: "8px",
            }}
          >
            {emailConfirmItem.label}
          </Typography>

          {/* Warning banner */}
          <Box sx={{ px: "16px", py: "8px" }}>
            <Stack
              direction="row"
              sx={{
                backgroundColor: orange[50],
                border: `1px solid ${orange[800]}`,
                borderRadius: "4px",
                px: "16px",
                py: "8px",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <InfoIcon sx={{ fontSize: "20px" }} htmlColor={orange[800]} />
              <Typography variant="body-small" sx={{ color: orange[800] }}>
                This is an external mail ID
              </Typography>
            </Stack>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" sx={{ px: "16px", py: "8px", gap: "8px" }}>
            <Button
              variant="secondary"
              size="small"
              onClick={handleCancelEmail}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleAddEmail}
              sx={{ flex: 1 }}
            >
              Add
            </Button>
          </Stack>
        </Paper>
      );
    }

    // ─── Normal member list view ─────────────────────────────────
    return (
      <Paper
        elevation={3}
        sx={{
          maxHeight: "240px",
          overflow: "auto",
          borderRadius: "8px",
          minWidth: "200px",
        }}
      >
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.lightBg.low,
            px: "12px",
            py: "8px",
            display: "block",
          }}
        >
          Suggestions
        </Typography>
        <MenuList ref={listRef} sx={{ py: "4px" }}>
          {selectableItems.map((item, index) => (
            <MenuItem
              key={item.id}
              onClick={() => selectItem(items.indexOf(item))}
              selected={index === selectedIndex}
              sx={{
                gap: "8px",
                py: "6px",
                px: "12px",
                borderRadius: "4px",
                mx: "4px",
                "&.Mui-selected": {
                  backgroundColor: theme.palette.neutral["005"],
                },
                "&:hover": {
                  backgroundColor: theme.palette.neutral["005"],
                },
              }}
            >
              {item.type === "email" ? (
                <>
                  <Stack
                    sx={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "100%",
                      bgcolor: theme.palette.neutral["010"],
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <MailIcon sx={{ fontSize: "12px" }} />
                  </Stack>
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.medium}
                  >
                    {item.label}
                  </Typography>
                </>
              ) : (
                <>
                  {item.memberType === "external" ? (
                    <Stack
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "100%",
                        bgcolor: theme.palette.neutral["010"],
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <MailIcon sx={{ fontSize: "12px" }} />
                    </Stack>
                  ) : (
                    <Avatar
                      variants="sm"
                      src={item.profileImage ?? ""}
                      fullname={item.label}
                    />
                  )}
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.medium}
                  >
                    {item.label}
                  </Typography>
                </>
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    );
  },
);

// ─── Suggestion item builders ─────────────────────────────────────────

const chatMemberToItem = (m: ChatMember): MentionItem => ({
  id: m.user_id,
  label: m.display_name || m.email || "Unknown",
  email: m.email,
  profileImage: m.profile_pic ?? null,
  type: "member",
  memberType: m.member_type,
});

const extractSearchText = (query: string): string => {
  const nameQuery = query.includes("@")
    ? query.slice(0, query.indexOf("@"))
    : query;
  return nameQuery.toLowerCase();
};

const mergeCustomerMembers = (
  memberItems: MentionItem[],
  customerMembers: MentionItem[],
): void => {
  const existingIds = new Set(memberItems.map((m) => m.id));
  const existingEmails = new Set(
    memberItems.map((m) => m.email?.toLowerCase()).filter(Boolean),
  );

  for (const cm of customerMembers) {
    if (existingIds.has(cm.id)) continue;
    if (cm.email && existingEmails.has(cm.email.toLowerCase())) continue;
    memberItems.push(cm);
  }
};

const buildEmailItem = (
  query: string,
  members: ChatMember[],
  customerMembers: MentionItem[],
): MentionItem | null => {
  if (!isValidEmail(query)) return null;

  const allEmails = new Set([
    ...members.map((m) => m.email?.toLowerCase()).filter(Boolean),
    ...customerMembers.map((m) => m.email?.toLowerCase()).filter(Boolean),
  ]);

  if (allEmails.has(query.toLowerCase())) return null;

  return {
    id: query,
    label: query,
    email: null,
    profileImage: null,
    type: "email",
  };
};

const NO_RESULTS_ITEM = (query: string, allowEmail = true): MentionItem[] => [
  {
    id: "__no_results__",
    label: query,
    email: null,
    profileImage: null,
    type: "no-results",
    allowEmail,
  },
];

// ─── Factory: creates mention suggestion config ──────────────────────

export const createMentionSuggestion = (
  membersRef: React.RefObject<ChatMember[]>,
  customerMembersRef: React.RefObject<MentionItem[]>,
  onQueryChangeRef: React.RefObject<(query: string) => void>,
  accessSpecifierRef: React.RefObject<AccessSpecifier>,
  disableExternal?: boolean,
): Partial<SuggestionOptions<MentionItem>> => ({
  findSuggestionMatch: findEmailAwareSuggestionMatch,

  items: ({ query }: { query: string }) => {
    onQueryChangeRef.current?.(query);

    const currentMode = accessSpecifierRef.current ?? "public";

    // Self mode: no mentions at all
    if (currentMode === "self") return [];

    const members = membersRef.current ?? [];
    const customerMembers = customerMembersRef.current ?? [];
    const searchText = extractSearchText(query);

    // Private mode: only resources; Public mode: all members
    const filteredMembers =
      currentMode === "private"
        ? members.filter((m) => m.member_type === "resource")
        : disableExternal
          ? members.filter((m) => m.member_type !== "external")
          : members;

    const memberItems: MentionItem[] = filteredMembers
      .map(chatMemberToItem)
      .filter((item) => item.label.toLowerCase().includes(searchText))
      .slice(0, 10);

    // Only merge customer members in public mode
    if (searchText.length > 0 && currentMode === "public") {
      mergeCustomerMembers(memberItems, customerMembers);
    }

    // Deduplicate by id to prevent React key warnings
    const seen = new Set<string>();
    const uniqueItems = memberItems.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

    // Only allow email mentions in public mode
    if (currentMode === "public" && !disableExternal) {
      const emailItem = buildEmailItem(query, members, customerMembers);
      if (emailItem) uniqueItems.push(emailItem);
    }

    return uniqueItems.length > 0
      ? uniqueItems
      : NO_RESULTS_ITEM(query, currentMode === "public");
  },

  render: () => {
    let component: ReactRenderer<MentionListRef>;
    let popup: TippyInstance[];

    return {
      onStart: (props: SuggestionProps<MentionItem>) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy("body", {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "top-start",
        });
      },

      onUpdate(props: SuggestionProps<MentionItem>) {
        component.updateProps(props);

        if (!props.clientRect) return;

        popup[0].setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        if (props.event.key === "Escape") {
          popup[0].hide();
          return true;
        }
        return component.ref?.onKeyDown(props.event) ?? false;
      },

      onExit() {
        popup[0]?.destroy();
        component?.destroy();
      },
    };
  },
});
